import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import styles from './styles.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { addNewAppointment } from '../../../../../Redux/appointments/appointmentSlice'
import { useTranslation } from 'react-i18next'
import { ReactComponent as StomatologyIcon } from '../../../../../assets/icons/stomatology.svg'
import { ReactComponent as PulmonologyIcon } from '../../../../../assets/icons/pulmonology.svg'

const FastServices = ({ close, data }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { operations } = useSelector((state) => state.operations)
  
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0]
  
  const [newAppointment, setNewAppointment] = useState({
    serviceName: '',
    date: today,
    time: '',
    selectedOperation: null
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [searchInput, setSearchInput] = useState('')
  const [searchCategory, setSearchCategory] = useState(['stomatology', 'pulmonology'])
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false)
  const menuRef = useRef(null)

  const handleClickOutside = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setServiceMenuOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleCategoryChange = (newCat) => {
    if (searchCategory.includes(newCat)) {
      setSearchCategory(searchCategory.filter((cat) => cat !== newCat))
    } else {
      setSearchCategory([...searchCategory, newCat])
    }
  }

  // Generate time options for every 30 minutes between 9:00 and 19:00
  const timeOptions = Array.from({ length: 21 }, (_, i) => {
    const hour = Math.floor((i * 30 + 540) / 60) // 540 minutes = 9:00
    const minute = (i * 30 + 540) % 60
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
  })

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewAppointment(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleServiceSelect = (operation) => {
    const price = operation.price || 0;
    setNewAppointment(prev => ({
      ...prev,
      serviceName: operation.op_name,
      selectedOperation: operation,
      price: price
    }))
    setServiceMenuOpen(false)
  }

  const filteredOperations = operations?.filter((op) => {
    const searchInputLower = searchInput.toLowerCase()
    const opNameLower = op.op_name?.toLowerCase()
    const opCategoryLower = op.category?.toLowerCase()
    return (
      (searchCategory.includes(opCategoryLower) || searchCategory.length === 0) &&
      (opNameLower?.includes(searchInputLower) || searchInputLower === '')
    )
  })

  const handleAppointmentSubmit = (e) => {
    e.preventDefault()
    
    // Prevent double submission
    if (isSubmitting) {
      return
    }
    
    setIsSubmitting(true)
    
    // Convert time to from/to format
    const [hours, minutes] = newAppointment.time.split(':')
    const from = parseFloat(hours) + parseFloat(minutes) / 60
    const to = from + 0.5 // 30 minutes duration
    // Get price from selected operation
    const selectedOp = newAppointment.selectedOperation
    const price = selectedOp && selectedOp.price ? Number(selectedOp.price) : 0
    
    // Shto pagesën në array_of_payments nëse është dhënë një pagesë
    let paymentsArray = [];
    if (newAppointment.patient_pay && newAppointment.date) {
      paymentsArray.push({
        amount: Number(newAppointment.patient_pay),
        value: Number(newAppointment.patient_pay),
        date: newAppointment.date
      });
    }
    
    // Llogarit totalin nga pagesat
    const totalFromPayments = paymentsArray.reduce((sum, payment) => 
      sum + (Number(payment.amount) || Number(payment.value) || 0), 0);
    
    const appointment = {
      date: newAppointment.date,
      from,
      to,
      fromStr: newAppointment.time,
      toStr: `${hours}:${parseInt(minutes) + 30}`,
      name: newAppointment.serviceName,
      services: [newAppointment.serviceName],
      operation_id: selectedOp?.id || Date.now().toString(),
      operation_name: newAppointment.serviceName,
      patient_id: data?.patient_id,
      patient_name: data?.patient_name,
      doctor_id: '',
      doctor_name: '',
      category: selectedOp?.category || 'general',
      color: selectedOp?.color || '#0288d1',
      service_cost: price,
      patient_pay: totalFromPayments, // Përdor totalin nga array
      difference: price - totalFromPayments,
      is_ongoing: true,
      unit_multiplier: 1,
      array_of_payments: paymentsArray,
      price: price,
      is_paid: totalFromPayments >= price,
      teeth_list: [],
      room: '',
      feedback: '',
      examination: '',
      therapy: '',
      description: '',
      payment_date: paymentsArray.length > 0 ? paymentsArray[0].date : null,
      is_fast_service: true
    }
    
    dispatch(addNewAppointment({
      ...appointment,
      close: () => {
        setIsSubmitting(false)
        close()
      }
    })).catch((error) => {
      console.error('Failed to create appointment:', error)
      setIsSubmitting(false)
    })
  }

  return (
    <div className={styles.popup_wrapper}>
      <div className={styles.form_header}>
        <h2 className={styles.header_text}>{t('fast_services')}</h2>
        <button className={styles.close_btn} onClick={close}>
          ✖
        </button>
      </div>
      <form className={styles.popup_from_container} onSubmit={handleAppointmentSubmit}>
        <div className={styles.form_entry}>
          <label className={styles.entry_label} htmlFor='serviceName'>
            {t('service_name')}
          </label>
          <div className={`${styles.op_selector_container} ${serviceMenuOpen ? styles.active_menu : ''}`} ref={menuRef}>
            <input
              type="text"
              id="serviceName"
              name="serviceName"
              value={newAppointment.serviceName}
              placeholder={t('select_service')}
              className={styles.selector_input}
              onClick={() => setServiceMenuOpen(!serviceMenuOpen)}
              readOnly
              required
            />
            <div className={styles.op_menu_container}>
              <div className={styles.op_menu_search}>
                <input
                  className={styles.op_menu_search_input}
                  value={searchInput}
                  placeholder={t('search')}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button
                  className={`${styles.op_category_search_btn} ${searchCategory.includes('stomatology') ? styles.active : ''}`}
                  type="button"
                  onClick={() => handleCategoryChange('stomatology')}
                >
                  <StomatologyIcon className={styles.btn_icon_sto} />
                </button>
                <button
                  className={`${styles.op_category_search_btn} ${searchCategory.includes('pulmonology') ? styles.active : ''}`}
                  type="button"
                  onClick={() => handleCategoryChange('pulmonology')}
                >
                  <PulmonologyIcon className={styles.btn_icon_pul} />
                </button>
              </div>
              <ul className={styles.op_menu_list_container}>
                {filteredOperations?.length ? (
                  filteredOperations.map((op) => (
                    <li
                      key={op.id}
                      className={styles.op_menu_item}
                      onClick={() => handleServiceSelect(op)}
                    >
                      <span className={styles.op_menu_text}>{op.op_name}</span>
                    </li>
                  ))
                ) : (
                  <li className={`${styles.op_menu_item} ${styles.disabled_entry}`} disabled>
                    <span className={styles.op_menu_text} style={{ textAlign: 'center', width: '100%' }}>
                      {t('no_services')}
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.form_entry}>
          <label className={styles.entry_label} htmlFor='date'>
            {t('date')}
          </label>
          <input
            type="date"
            name="date"
            value={newAppointment.date}
            onChange={handleInputChange}
            required
            className={styles.entry_input}
          />
        </div>
        <div className={styles.form_entry}>
          <label className={styles.entry_label} htmlFor='time'>
            {t('from')}
          </label>
          <select
            name="time"
            value={newAppointment.time}
            onChange={handleInputChange}
            required
            className={styles.entry_input}
          >
            <option value="">{t('select_time')}</option>
            {timeOptions.map(time => (
              <option key={time} value={time}>{time}</option>
            ))}
          </select>
        </div>
        <div className={styles.form_entry}>
          <button 
            className={styles.submit_button} 
            type='submit'
            disabled={isSubmitting}
          >
            {isSubmitting ? t('creating_appointment') || 'Duke krijuar...' : t('book_appointment')}
          </button>
        </div>
      </form>
    </div>
  )
}

FastServices.propTypes = {
  close: PropTypes.func.isRequired,
  data: PropTypes.shape({
    patient_id: PropTypes.string.isRequired,
    patient_name: PropTypes.string.isRequired
  }).isRequired
}

export default FastServices 